import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  PageContainer,
  Button,
  Checkbox,
  DataTable,
  ErrorBoundary,
  Input,
  Modal,
  Select,
  useToast,
} from '@bbpos/ui';
import { productMasterService } from '@/services/product-master.service';
import { productService } from '@/services/product.service';
import { locationService } from '@/services/location.service';
import { useAuthStore } from '@/stores/use-auth-store';
import { ROLE_LEVELS } from '@/constants/product';
import { GLASS_FILTER, cn } from '@/constants/design-tokens';
import { useTenantCurrency } from '@/hooks/use-tenant-currency';
import { MasterStoresPanel } from '@/components/features/products/MasterStoresPanel';
import type { ProductMasterListItem } from '@/types/product-master';
import { makeMasterColumns } from './master-columns';

const PAGE_SIZE = 20;

/** Where a master row opens: the product page of one of its store rows, or the wizard for a draft. */
export function masterProductHref(row: ProductMasterListItem): string | null {
  if (!row.product_id) return null;
  return row.is_draft ? `/products/new?draft=${row.product_id}` : `/products/${row.product_id}`;
}

function Icon({ d }: { d: string }) {
  return (
    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d={d} />
    </svg>
  );
}

function KpiCard({
  title,
  value,
  icon,
  iconBg,
  tint,
  onClick,
}: {
  title: string;
  value: number | undefined;
  icon: React.ReactNode;
  iconBg: string;
  tint: string;
  onClick?: () => void;
}) {
  const Wrapper = onClick ? 'button' : 'div';
  return (
    <Wrapper
      type={onClick ? 'button' : undefined}
      onClick={onClick}
      className={cn(
        'relative overflow-hidden rounded-xl p-5 text-left shadow-md transition-all duration-200 hover:shadow-lg animate-slide-up',
        tint,
        onClick && 'cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500',
      )}
    >
      <div className="mb-3 flex items-center gap-3">
        <div className={cn('flex h-10 w-10 items-center justify-center rounded-lg text-white shadow-sm', iconBg)}>{icon}</div>
        <span className="text-sm font-medium text-neutral-500 dark:text-neutral-400">{title}</span>
      </div>
      <p className="text-2xl font-bold tabular-nums text-neutral-900 dark:text-white">
        {value === undefined ? '—' : value.toLocaleString('en-IN')}
      </p>
    </Wrapper>
  );
}

type BulkMode = 'enable' | 'disable';

function MasterCatalogueContent() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const currency = useTenantCurrency();
  const [searchParams, setSearchParams] = useSearchParams();
  const profile = useAuthStore((s) => s.profile);
  const isOwner = (ROLE_LEVELS[profile?.role ?? ''] ?? 0) >= ROLE_LEVELS.OWNER;

  const page = Number(searchParams.get('page') ?? '1');
  const search = searchParams.get('search') ?? '';
  const category = searchParams.get('category') ?? '';
  const [searchInput, setSearchInput] = useState(search);
  const [selected, setSelected] = useState<string[]>([]);
  const [storesFor, setStoresFor] = useState<ProductMasterListItem | null>(null);
  const [bulk, setBulk] = useState<{ mode: BulkMode; ids: string[] } | null>(null);
  const [bulkStores, setBulkStores] = useState<string[]>([]);

  function setParam(key: string, value: string) {
    const params = new URLSearchParams(searchParams);
    if (value) params.set(key, value);
    else params.delete(key);
    if (key !== 'page') params.delete('page');
    setSearchParams(params);
  }

  // Debounced search; only rewrites the URL when the typed term really changed,
  // so paging (which also changes searchParams) never snaps back to page 1.
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchInput === (searchParams.get('search') ?? '')) return;
      const params = new URLSearchParams(searchParams);
      if (searchInput) params.set('search', searchInput);
      else params.delete('search');
      params.delete('page');
      setSearchParams(params);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput, searchParams, setSearchParams]);

  const { data: summary } = useQuery({
    queryKey: ['product-masters', 'summary'],
    queryFn: () => productMasterService.summary(),
    staleTime: 15_000,
  });

  const { data, isLoading, error } = useQuery({
    queryKey: ['product-masters', 'list', page, search, category],
    queryFn: () =>
      productMasterService.list({
        page,
        limit: PAGE_SIZE,
        search: search || undefined,
        category: category || undefined,
      }),
    staleTime: 15_000,
  });

  const { data: categories } = useQuery({
    queryKey: ['product-categories'],
    queryFn: () => productService.getCategories(),
    staleTime: 60_000,
  });

  // An Owner gets every store; a Manager gets the stores they are assigned to,
  // which are the only ones the API lets them change.
  const { data: locData } = useQuery({
    queryKey: ['locations', 'all'],
    queryFn: () => locationService.list({ limit: 100 }),
    staleTime: 60_000,
  });
  const myStores = (locData?.data ?? []).filter((l) => l.is_active);

  const bulkMutation = useMutation({
    mutationFn: ({ mode, ids, stores }: { mode: BulkMode; ids: string[]; stores: string[] }) =>
      productMasterService.setLocationsBulk({
        master_ids: ids,
        ...(mode === 'enable' ? { enable: stores } : { disable: stores }),
      }),
    onSuccess: (res, { mode, ids, stores }) => {
      toast({
        title:
          mode === 'enable'
            ? `${ids.length} product(s) now sold at ${stores.length} store(s)`
            : `${ids.length} product(s) no longer sold at ${stores.length} store(s)`,
        description:
          mode === 'enable'
            ? `${res.enabled.filter((r) => r.created).length} new store listing(s) added with stock 0.`
            : undefined,
        variant: 'success',
      });
      setBulk(null);
      setSelected([]);
    },
    onError: (err: Error) => {
      toast({ title: err.message || 'Could not change the stores', variant: 'error' });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['product-masters'] });
      queryClient.invalidateQueries({ queryKey: ['product-master'] });
      queryClient.invalidateQueries({ queryKey: ['products'] });
    },
  });

  const columns = useMemo(
    () =>
      makeMasterColumns({
        currency,
        storeTotal: summary?.stores ?? 0,
        onOpen: (row) => {
          const href = masterProductHref(row);
          if (href) navigate(href);
          else setStoresFor(row);
        },
        onStores: (row) => setStoresFor(row),
      }),
    [currency, summary?.stores, navigate],
  );

  function openBulk(mode: BulkMode, ids: string[]) {
    setBulkStores([]);
    setBulk({ mode, ids });
  }

  const masters = data?.data ?? [];
  const hasFilters = Boolean(search || category);

  return (
    <>
      <div className="mb-6 rounded-xl bg-gradient-to-r from-teal-600 to-success-600 p-6 text-white shadow-lg animate-fade-in">
        <h2 className="text-2xl font-bold text-white">Master Catalogue</h2>
        <p className="mt-1 text-teal-100">
          One list of products for all your stores. Choose which stores sell each one.
        </p>
      </div>

      <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-4 lg:gap-6">
        <KpiCard
          title="Products"
          value={summary?.masters}
          iconBg="bg-gradient-to-br from-teal-500 to-teal-600"
          tint="bg-teal-50 dark:bg-teal-900/20"
          icon={<Icon d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />}
        />
        <KpiCard
          title="Active"
          value={summary?.active}
          iconBg="bg-gradient-to-br from-success-500 to-success-600"
          tint="bg-success-50 dark:bg-success-900/20"
          icon={<Icon d="M5 13l4 4L19 7" />}
        />
        <KpiCard
          title="In every store"
          value={summary?.everywhere}
          iconBg="bg-gradient-to-br from-brand-500 to-indigo-500"
          tint="bg-brand-50 dark:bg-brand-900/20"
          icon={<Icon d="M3 7h18M5 7v12h14V7M9 11h6" />}
        />
        <KpiCard
          title="Open conflicts"
          value={summary?.open_conflicts}
          iconBg="bg-gradient-to-br from-warning-500 to-danger-500"
          tint="bg-warning-50 dark:bg-warning-900/20"
          icon={<Icon d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />}
          onClick={isOwner ? () => navigate('/products/masters/conflicts') : undefined}
        />
      </div>

      <div className={cn(GLASS_FILTER, 'mb-6 p-3')}>
        <div className="flex flex-col items-stretch gap-3 sm:flex-row sm:items-center">
          <div className="w-full sm:w-72">
            <Input
              variant="search"
              placeholder="Search name, SKU or barcode…"
              value={searchInput}
              onChange={(v) => setSearchInput(v)}
              aria-label="Search the master catalogue"
            />
          </div>
          <div className="w-full sm:w-48">
            <Select
              options={[{ value: '', label: 'All Categories' }, ...(categories ?? []).map((c) => ({ value: c, label: c }))]}
              value={category}
              onChange={(v) => setParam('category', typeof v === 'string' ? v : '')}
              placeholder="Category"
            />
          </div>
          {selected.length > 0 && (
            <div className="flex flex-wrap gap-2 sm:ml-auto">
              <Button size="sm" variant="primary" onClick={() => openBulk('enable', selected)}>
                Sell at stores ({selected.length})
              </Button>
              <Button size="sm" variant="secondary" onClick={() => openBulk('disable', selected)}>
                Stop selling ({selected.length})
              </Button>
            </div>
          )}
        </div>
      </div>

      <DataTable<ProductMasterListItem>
        columns={columns}
        data={masters}
        loading={isLoading}
        error={error ? 'Could not load the master catalogue. Please try again.' : null}
        totalCount={data?.meta?.total ?? 0}
        currentPage={page}
        pageSize={PAGE_SIZE}
        onPageChange={(p) => setParam('page', String(p))}
        selectable
        selectedRows={selected}
        onSelectionChange={setSelected}
        emptyState={{
          title: hasFilters ? 'No products match these filters' : 'No products yet',
          description: hasFilters
            ? 'Clear the search or category to see every product.'
            : 'Products you add appear here, once for all your stores.',
        }}
        stickyHeader
      />

      <Modal open={storesFor !== null} onClose={() => setStoresFor(null)} title={storesFor?.name} variant="wide">
        {storesFor && <MasterStoresPanel masterId={storesFor.id} />}
      </Modal>

      <Modal
        open={bulk !== null}
        onClose={() => setBulk(null)}
        title={
          bulk?.mode === 'enable'
            ? `Sell ${bulk.ids.length} product(s) at…`
            : `Stop selling ${bulk?.ids.length ?? 0} product(s) at…`
        }
        footer={
          <>
            <Button variant="ghost" onClick={() => setBulk(null)}>
              Cancel
            </Button>
            <Button
              variant={bulk?.mode === 'disable' ? 'danger' : 'primary'}
              disabled={bulkStores.length === 0}
              loading={bulkMutation.isPending}
              onClick={() => bulk && bulkMutation.mutate({ ...bulk, stores: bulkStores })}
            >
              {bulk?.mode === 'enable' ? 'Sell here' : 'Stop selling'}
            </Button>
          </>
        }
      >
        {myStores.length === 0 ? (
          <p className="text-sm text-neutral-500 dark:text-neutral-400">You have no active stores to choose.</p>
        ) : (
          <div className="space-y-3">
            <Checkbox
              label="All stores"
              checked={bulkStores.length === myStores.length}
              indeterminate={bulkStores.length > 0 && bulkStores.length < myStores.length}
              onChange={(on) => setBulkStores(on ? myStores.map((l) => l.id) : [])}
            />
            <div className="space-y-2 border-t border-neutral-100 pt-3 dark:border-neutral-700">
              {myStores.map((l) => (
                <Checkbox
                  key={l.id}
                  label={l.location_code ? `${l.name} (${l.location_code})` : l.name}
                  checked={bulkStores.includes(l.id)}
                  onChange={(on) =>
                    setBulkStores((s) => (on ? [...s, l.id] : s.filter((id) => id !== l.id)))
                  }
                />
              ))}
            </div>
            {bulk?.mode === 'enable' && (
              <p className="text-xs text-neutral-500 dark:text-neutral-400">
                A store that never sold a product starts it with stock 0 and the master price.
              </p>
            )}
          </div>
        )}
      </Modal>
    </>
  );
}

/** Download the master list as a CSV the product import reads back (one row per product, plus its stores). */
function ExportButton() {
  const { toast } = useToast();
  const [busy, setBusy] = useState(false);

  async function handleExport() {
    setBusy(true);
    try {
      const { blob, filename } = await productMasterService.exportCsv();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename ?? 'product-masters.csv';
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast({ title: (err as Error).message || 'Export failed', variant: 'error' });
    } finally {
      setBusy(false);
    }
  }

  return (
    <Button variant="secondary" loading={busy} onClick={handleExport}>
      Export CSV
    </Button>
  );
}

export function MasterCataloguePage() {
  const navigate = useNavigate();
  const profile = useAuthStore((s) => s.profile);
  const isOwner = (ROLE_LEVELS[profile?.role ?? ''] ?? 0) >= ROLE_LEVELS.OWNER;
  return (
    <PageContainer
      title="Master Catalogue"
      subtitle="Every product once, and the stores that sell it"
      breadcrumbs={[{ label: 'Products', href: '/products' }, { label: 'Master Catalogue' }]}
      actions={
        <div className="flex gap-2">
          <ExportButton />
          {isOwner && (
            <Button variant="secondary" onClick={() => navigate('/products/masters/conflicts')}>
              Review conflicts
            </Button>
          )}
        </div>
      }
    >
      <ErrorBoundary>
        <MasterCatalogueContent />
      </ErrorBoundary>
    </PageContainer>
  );
}
